<script setup lang="ts">
/**
 * An assistant's compliance record, edited where the assistant is edited.
 *
 * This was the Governance tab's drawer: a second editor for the same
 * assistant, on another tab, with its own Save. It is now the Compliance
 * section of the assistant editor (2026-09-24 review), and the Governance
 * tab became the read-only Compliance report. Same fields, same endpoints,
 * same audit — one place to change them.
 *
 * What is here: owner and classification, the two per-assistant
 * guardrails, configuration approval, the controls and the last N days of
 * content-free evidence. Access and the data owner's confirmation live
 * under "Who can use it", beside the groups they are about.
 *
 * The parent owns the drawer's buttons, so `save`, `dirty` and `saving`
 * are exposed for its one Save.
 */
import { vWrite } from "../admin-read-only";
import { computed, ref, watch } from "vue";
import {
  AdminError,
  type ControlStatus,
  type GovernanceAssistant,
  type GovernanceControl,
  type GovernanceResponse,
  type GovernanceTurnRow,
  type PiiRedactionMode,
  type RiskClass,
  type SensitiveInputMode,
  type RolePerson,
  approveGovernanceConfig,
  getGovernanceTurns,
  getResponsibilities,
  saveGovernance,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import {
  STATUS_LABELS,
  STATUS_TONE,
  approvalKind,
  approverName,
  fmtDate,
  fmtTs,
  fmtWhen,
  frameworkTags,
} from "../utils/governance";
import FoldSection from "./FoldSection.vue";
import ReadinessSteps from "./ReadinessSteps.vue";
import SkeletonRows from "./SkeletonRows.vue";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{
  entry: GovernanceAssistant;
  controls: GovernanceControl[];
  deployment: GovernanceResponse["deployment"];
  windowDays: number;
  devUser: string;
}>();
const emit = defineEmits<{ (e: "updated", a: GovernanceAssistant): void }>();

const toast = useToast();
const { confirm } = useConfirm();

function _toastError(e: unknown, context: string): void {
  toast.error(context, e instanceof AdminError ? e.message : String(e));
}

const controlsById = computed(
  () => new Map(props.controls.map((c) => [c.id, c])),
);
function controlTitle(id: string): string {
  return controlsById.value.get(id)?.title ?? id;
}
function tagsFor(id: string): string[] {
  return frameworkTags(controlsById.value.get(id));
}

// ── Form ──────────────────────────────────────────────────────────────────

/** Dates are strings ("" = unset) because that is what a date input binds
 * to; they go back to null on save. */
type Draft = {
  risk_class: RiskClass;
  risk_rationale: string;
  data_classes: string[];
  lawful_basis: string;
  assessment_ref: string;
  intended_use: string;
  out_of_scope_uses: string;
  notes: string;
  pii_redaction: PiiRedactionMode;
  sensitive_input: SensitiveInputMode;
  owner: string;
  co_owners: string;
};

function splitPeople(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function draftFrom(a: GovernanceAssistant): Draft {
  const g = a.governance;
  return {
    risk_class: g.risk_class ?? "unclassified",
    risk_rationale: g.risk_rationale ?? "",
    data_classes: [...(g.data_classes ?? [])].sort(),
    lawful_basis: g.lawful_basis ?? "",
    assessment_ref: g.assessment_ref ?? "",
    intended_use: g.intended_use ?? "",
    out_of_scope_uses: g.out_of_scope_uses ?? "",
    notes: g.notes ?? "",
    pii_redaction: a.guardrails?.pii_redaction ?? "inherit",
    sensitive_input: a.guardrails?.sensitive_input ?? "inherit",
    owner: a.owner_email || a.owner_oid || "",
    co_owners: (a.co_owner_emails ?? a.co_owner_oids ?? []).join(", "),
  };
}

const draft = ref<Draft>(draftFrom(props.entry));
const approveNote = ref("");

// A different assistant resets the form; the same one refreshed (after a
// save or an approval) leaves an edit in progress alone.
watch(
  () => props.entry.slug,
  () => {
    draft.value = draftFrom(props.entry);
    approveNote.value = "";
    void loadTurns(props.entry.slug);
  },
);

const dirty = computed(
  () =>
    JSON.stringify({
      ...draft.value,
      data_classes: [...draft.value.data_classes].sort(),
    }) !== JSON.stringify(draftFrom(props.entry)),
);

const DATA_CLASSES: { value: string; label: string }[] = [
  { value: "personal", label: "Personal data" },
  { value: "special_category", label: "Special category (Art. 9)" },
  { value: "contractual", label: "Contractual" },
  { value: "financial", label: "Financial" },
  { value: "hr", label: "HR / employee" },
  { value: "customer", label: "Customer" },
  { value: "public", label: "Public" },
  { value: "internal", label: "Internal" },
];

const LAWFUL_BASES: { value: string; label: string }[] = [
  { value: "", label: "Not set" },
  { value: "legitimate_interest", label: "Legitimate interest (Art. 6(1)(f))" },
  { value: "contract", label: "Contract (Art. 6(1)(b))" },
  { value: "consent", label: "Consent (Art. 6(1)(a))" },
  { value: "legal_obligation", label: "Legal obligation (Art. 6(1)(c))" },
  { value: "public_task", label: "Public task (Art. 6(1)(e))" },
];

function toggleDataClass(value: string): void {
  const set = new Set(draft.value.data_classes);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  draft.value.data_classes = Array.from(set).sort();
}

const guardrailSummary = computed(() => {
  const d = draft.value;
  return [
    `PII: ${d.pii_redaction}`,
    `sensitive input: ${d.sensitive_input}`,
    props.entry.guardrails?.abstain_enabled ? "abstains when unsure" : "",
  ]
    .filter(Boolean)
    .join(" · ");
});

const saving = ref(false);

/** True when saved; false when cancelled or refused. */
async function save(): Promise<boolean> {
  const a = props.entry;
  const d = draft.value;
  if (saving.value) return false;
  if (
    d.pii_redaction === "off" &&
    (a.guardrails?.pii_redaction ?? "inherit") !== "off"
  ) {
    const ok = await confirm({
      title: "Turn off PII redaction?",
      message: `${a.name} will return answers without output redaction, whatever the platform default is. This is recorded in the audit log.`,
      confirmLabel: "Turn off",
      danger: true,
    });
    if (!ok) return false;
  }
  // Owners are sent only when they changed: resolving an e-mail can need
  // the directory, and an unrelated edit should not depend on it.
  const was = draftFrom(a);
  const people: { owner_email?: string; co_owner_emails?: string[] } = {};
  if (d.owner.trim() !== was.owner) people.owner_email = d.owner.trim();
  if (
    splitPeople(d.co_owners).join(",") !== splitPeople(was.co_owners).join(",")
  )
    people.co_owner_emails = splitPeople(d.co_owners);
  if (people.owner_email === "" && was.owner) {
    const ok = await confirm({
      title: "Remove the owner?",
      message: `${a.name} will have no named owner, which the review pack reports as a gap.`,
      confirmLabel: "Remove owner",
      danger: true,
    });
    if (!ok) return false;
  }
  saving.value = true;
  try {
    const updated = await saveGovernance(
      a.slug,
      {
        risk_class: d.risk_class,
        risk_rationale: d.risk_rationale,
        data_classes: d.data_classes,
        lawful_basis: d.lawful_basis,
        assessment_ref: d.assessment_ref,
        intended_use: d.intended_use,
        out_of_scope_uses: d.out_of_scope_uses,
        notes: d.notes,
        guardrails: {
          pii_redaction: d.pii_redaction,
          sensitive_input: d.sensitive_input,
        },
        ...people,
      },
      props.devUser,
    );
    emit("updated", updated);
    draft.value = draftFrom(updated);
    toast.success(`Compliance saved for ${updated.name || updated.slug}`);
    return true;
  } catch (e) {
    _toastError(e, "Could not save");
    return false;
  } finally {
    saving.value = false;
  }
}

// ── Acceptance for use (ADR 0066) ─────────────────────────────────────────

const approving = ref(false);
const NOTE_MAX = 500;
const board = ref<RolePerson[]>([]);
const acceptedBy = ref("");

async function loadBoard(): Promise<void> {
  try {
    const r = await getResponsibilities(props.devUser);
    board.value = r.roles.find((x) => x.key === "ai-board")?.people ?? [];
    if (!acceptedBy.value && board.value.length)
      acceptedBy.value = board.value[0].email || board.value[0].name;
  } catch {
    board.value = [];
  }
}
void loadBoard();

const approvalSummary = computed(() => {
  const a = props.entry;
  const kind = approvalKind(a);
  const who = approverName(a.approval);
  const when = fmtDate(a.approval?.approved_at ?? null);
  if (kind === "never") return "not yet accepted";
  if (kind === "changed") return `changed since ${who} accepted it (${when})`;
  if (kind === "due") return `${who}'s acceptance is due for review`;
  return `accepted by ${who}, ${when}`;
});

async function approve(): Promise<void> {
  const a = props.entry;
  if (approving.value) return;
  const person = board.value.find(
    (p) => (p.email || p.name) === acceptedBy.value,
  );
  const ok = await confirm({
    title: "Record the AI Board's acceptance?",
    message: `This records that ${person?.name ?? acceptedBy.value} accepted ${a.name || a.slug} for use as it runs now, as decided at: “${approveNote.value.trim()}”. It lasts a year, or until the assistant changes.`,
    confirmLabel: "Record acceptance",
  });
  if (!ok) return;
  approving.value = true;
  try {
    const updated = await approveGovernanceConfig(
      a.slug,
      acceptedBy.value,
      approveNote.value.trim(),
      props.devUser,
    );
    // The form is left alone: accepting must not discard an unsaved edit.
    emit("updated", updated);
    approveNote.value = "";
    toast.success(`${updated.name || updated.slug} accepted for use`);
  } catch (e) {
    _toastError(e, "Could not record the acceptance");
  } finally {
    approving.value = false;
  }
}

// ── Controls and evidence ─────────────────────────────────────────────────

/** In words, like the Evidence page: what is in force, not what is "to
 * fix" — the decisions are the three steps above. */
const controlSummary = computed<{ text: string }>(() => {
  const cs = props.entry.controls ?? [];
  const n = (st: ControlStatus) => cs.filter((c) => c.status === st).length;
  return {
    text: [
      `${n("ok")} in force`,
      n("warn") ? `${n("warn")} partly` : "",
      n("gap") ? `${n("gap")} not in force` : "",
    ]
      .filter(Boolean)
      .join(" · "),
  };
});

const ledgerSummary = computed(() => {
  const c = props.entry.counts;
  if (!c?.turns) return "no turns";
  return [
    `${c.turns} ${c.turns === 1 ? "turn" : "turns"}`,
    c.errors ? `${c.errors} errors` : "",
    c.abstained ? `${c.abstained} abstained` : "",
  ]
    .filter(Boolean)
    .join(" · ");
});

const TURNS_DAYS = 7;
const turns = ref<GovernanceTurnRow[]>([]);
const turnsLoading = ref(false);

async function loadTurns(slug: string): Promise<void> {
  turnsLoading.value = true;
  turns.value = [];
  try {
    const r = await getGovernanceTurns(
      { assistant: slug, days: TURNS_DAYS, limit: 25 },
      props.devUser,
    );
    if (props.entry.slug === slug) turns.value = r.rows;
  } catch (e) {
    _toastError(e, "Could not load recent evidence");
  } finally {
    turnsLoading.value = false;
  }
}
void loadTurns(props.entry.slug);

function num(v: number | undefined): string {
  return v === undefined ? "—" : String(v);
}

const COUNT_LABELS: {
  key: keyof GovernanceAssistant["counts"];
  label: string;
}[] = [
  { key: "turns", label: "Turns" },
  { key: "errors", label: "Errors" },
  { key: "turns_with_evidence", label: "With evidence" },
  { key: "pii_redacted_turns", label: "PII redacted" },
  { key: "sensitive_input_turns", label: "Sensitive input" },
  { key: "abstained", label: "Abstained" },
  { key: "citations_dropped", label: "Citations dropped" },
];

defineExpose({ save, dirty, saving });
</script>

<template>
  <div class="drawer">
    <!-- (b) Acceptance for use (ADR 0066) -->
    <FoldSection
      title="Before it may be used"
      :summary="
        entry.readiness
          ? entry.readiness.ready
            ? approvalSummary
            : `${entry.readiness.steps.filter((s) => s.status === 'todo').length} of 3 steps open`
          : approvalSummary
      "
      :open="approvalKind(entry) !== 'current'"
      :tone="approvalKind(entry) === 'current' ? 'neutral' : 'warn'"
    >
      <ReadinessSteps v-if="entry.readiness" :readiness="entry.readiness" />
      <p v-if="entry.approval?.accepted && entry.approval?.note" class="hint">
        Recorded with: {{ entry.approval.note }}
        <template v-if="entry.approval.recorded_by_email">
          (entered by {{ entry.approval.recorded_by_email }})
        </template>
      </p>
      <p
        v-if="entry.approval?.approved && !entry.approval?.accepted"
        class="hint"
      >
        Configuration approved by an admin on
        {{ fmtWhen(entry.approval?.approved_at) }}, before the AI Board
        accepted assistants — kept as history, not counted.
      </p>
      <template v-if="approvalKind(entry) !== 'current'">
        <p class="hint">
          AI Board members accept for themselves under <strong>My
          sign-offs</strong>. Record it here only for a decision taken
          elsewhere, such as a meeting — name the member and where it is
          written down.
        </p>
        <div class="approval__form">
          <label class="field approval__field">
            AI Board member
            <select v-model="acceptedBy" :disabled="!board.length">
              <option
                v-for="p in board"
                :key="p.email || p.name"
                :value="p.email || p.name"
              >
                {{ p.name }}{{ p.email ? ` (${p.email})` : "" }}
              </option>
            </select>
          </label>
          <label class="field approval__field">
            Where the decision is recorded
            <input
              type="text"
              v-model="approveNote"
              :maxlength="NOTE_MAX"
              placeholder="AI Board meeting 2026-10-02, minutes item 3"
            />
          </label>
          <button
            class="btn btn--primary"
            :disabled="
              approving ||
              !approveNote.trim() ||
              !acceptedBy ||
              entry.readiness?.can_accept === false
            "
            :title="
              entry.readiness?.can_accept === false
                ? 'The data owners and the access tests come first'
                : ''
            "
            v-write
            @click="approve"
          >
            {{ approving ? "Recording…" : "Record acceptance" }}
          </button>
        </div>
        <p v-if="!board.length" class="hint">
          Name the AI Board under Approvals → Who signs first.
        </p>
      </template>
      <p
        v-if="entry.approval?.fingerprint"
        class="muted small"
        :title="entry.approval.fingerprint"
      >
        Configuration id
        <span class="mono">{{ entry.approval.fingerprint.slice(0, 12) }}</span>
      </p>
    </FoldSection>

    <!-- (a) Governance form -->
    <section class="drawer__section">
      <h4>The record</h4>
      <p class="hint">
        What the AI Board reads before it accepts. Owner, risk class and
        intended use are required for that; saved with the editor’s Save.
      </p>
      <div class="form">
        <div class="grid2">
          <label class="field">
            Owner
            <input
              type="text"
              v-model="draft.owner"
              placeholder="name@danofficeit.com"
              autocomplete="off"
            />
          </label>
          <label class="field">
            Co-owners
            <input
              type="text"
              v-model="draft.co_owners"
              placeholder="e-mails, comma separated"
              autocomplete="off"
            />
          </label>
        </div>
        <p class="hint">
          Owners edit the prompt refinements and, on assistants with their own
          page, the defaults there (the Servicekatalog's prices). Set here, they
          take precedence over the YAML.
        </p>
        <label class="field">
          Risk class (EU AI Act)
          <select v-model="draft.risk_class">
            <option value="unclassified">Unclassified — gap</option>
            <option value="minimal">Minimal</option>
            <option value="limited">Limited (transparency duties)</option>
            <option value="high">High (Annex III)</option>
          </select>
        </label>
        <label class="field">
          Risk rationale
          <textarea
            rows="3"
            v-model="draft.risk_rationale"
            placeholder="Why this class — what the assistant decides or informs, and for whom."
          ></textarea>
        </label>
        <div class="field">
          Data classes
          <div class="checks">
            <label
              v-for="dc in DATA_CLASSES"
              :key="dc.value"
              class="check"
              :class="{
                'check--on': draft.data_classes.includes(dc.value),
              }"
            >
              <input
                type="checkbox"
                :checked="draft.data_classes.includes(dc.value)"
                @change="toggleDataClass(dc.value)"
              />
              {{ dc.label }}
            </label>
          </div>
        </div>
        <label class="field">
          Lawful basis (GDPR)
          <select v-model="draft.lawful_basis">
            <option
              v-for="lb in LAWFUL_BASES"
              :key="lb.value"
              :value="lb.value"
            >
              {{ lb.label }}
            </option>
          </select>
        </label>
        <label class="field">
          Assessment reference
          <input
            type="text"
            v-model="draft.assessment_ref"
            placeholder="AI001 row, DPIA id or ticket"
          />
        </label>
        <label class="field">
          Intended use
          <textarea
            rows="3"
            v-model="draft.intended_use"
            placeholder="What it is for and who uses it."
          ></textarea>
        </label>
        <label class="field">
          Out-of-scope uses
          <textarea
            rows="3"
            v-model="draft.out_of_scope_uses"
            placeholder="What it must not be used for."
          ></textarea>
        </label>
        <p class="hint">
          Next review:
          <strong>{{
            entry.governance.next_review_at
              ? fmtDate(entry.governance.next_review_at)
              : "set when the AI Board accepts it"
          }}</strong>
          — a year after acceptance, or at the next review round.
        </p>
        <label class="field">
          Notes
          <textarea rows="2" v-model="draft.notes"></textarea>
        </label>
      </div>

      <!-- Set when an assistant is built and rarely after. The line says
               what it is set to, which is usually all anyone wants to know. -->
      <FoldSection title="Guardrails" :summary="guardrailSummary">
        <p class="hint">
          Per-assistant overrides of the platform defaults. Abstention and
          citation validation are set under Setup → Advanced and shown here
          read-only.
        </p>
        <div class="form">
          <div class="grid2">
            <label class="field">
              PII redaction (output)
              <select v-model="draft.pii_redaction">
                <option value="inherit">
                  Inherit platform ({{
                    deployment.pii_redact_enabled ? deployment.pii_mode : "off"
                  }})
                </option>
                <option value="off">Off</option>
                <option value="regex">Regex (local)</option>
                <option value="azure">Azure AI Language</option>
              </select>
            </label>
            <label class="field">
              Sensitive input (CPR etc.)
              <select v-model="draft.sensitive_input">
                <option value="inherit">
                  Inherit platform ({{
                    deployment.sensitive_input_check ? "warn" : "off"
                  }})
                </option>
                <option value="warn">Warn</option>
                <option value="block">Block</option>
                <option value="off">Off</option>
              </select>
            </label>
          </div>
          <dl class="readonly">
            <div>
              <dt>Abstention</dt>
              <dd>
                <template v-if="entry.guardrails?.abstain_enabled">
                  on · threshold {{ entry.guardrails.abstain_threshold }}
                </template>
                <span v-else class="muted">off</span>
              </dd>
            </div>
            <div>
              <dt>Citation validation</dt>
              <dd>
                <template v-if="entry.guardrails?.validation_enabled">
                  on
                </template>
                <span v-else class="muted">off</span>
              </dd>
            </div>
          </dl>
        </div>
      </FoldSection>
    </section>

    <!-- (c) Controls -->
    <FoldSection
      class="drawer__fold"
      title="Safeguards (evidence)"
      :summary="controlSummary.text"
    >
      <p class="hint">
        Computed from live settings and the turn ledger. A reference such as
        <code class="fw">EU AI Act Art. 26</code> names the clause the control
        speaks to; it is not a statement that the clause is met.
      </p>
      <ul class="controls">
        <li v-for="c in entry.controls" :key="c.id" class="control">
          <StatusChip :tone="STATUS_TONE[c.status]" dot>
            {{ STATUS_LABELS[c.status] }}
          </StatusChip>
          <div class="control__body">
            <div class="control__title">{{ controlTitle(c.id) }}</div>
            <div class="muted small">{{ c.detail }}</div>
            <div v-if="tagsFor(c.id).length" class="fw-tags">
              <code v-for="t in tagsFor(c.id)" :key="t" class="fw">{{
                t
              }}</code>
            </div>
          </div>
        </li>
        <li v-if="entry.controls.length === 0" class="muted">
          No assistant-scoped controls in the catalogue.
        </li>
      </ul>
    </FoldSection>

    <!-- (d) Ledger -->
    <FoldSection
      class="drawer__fold"
      :title="`Last ${windowDays} days`"
      :summary="ledgerSummary"
    >
      <dl class="counts">
        <div v-for="c in COUNT_LABELS" :key="c.key">
          <dt>{{ c.label }}</dt>
          <dd>{{ entry.counts[c.key] }}</dd>
        </div>
      </dl>

      <h4>Recent evidence</h4>
      <p class="hint">
        Last {{ TURNS_DAYS }} days, newest first, content-free — ids, hashes and
        counts only.
      </p>
      <SkeletonRows v-if="turnsLoading" :rows="4" />
      <div v-else class="table-wrap">
        <table class="admin-table evidence">
          <thead>
            <tr>
              <th>When</th>
              <th>Transport</th>
              <th>Model</th>
              <th class="num">Hits</th>
              <th class="num">Redactions</th>
              <th>Abstained</th>
              <th class="num">Cit. dropped</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in turns" :key="t.evidence.request_id ?? t.ts">
              <td class="mono small">{{ fmtTs(t.ts) }}</td>
              <td>{{ t.evidence.transport ?? "—" }}</td>
              <td class="mono small">{{ t.model || "—" }}</td>
              <td class="num">{{ num(t.evidence.hits_returned) }}</td>
              <td class="num">{{ num(t.evidence.pii_redacted_count) }}</td>
              <td>
                <template v-if="t.evidence.abstained === undefined">—</template>
                <template v-else>{{
                  t.evidence.abstained ? "yes" : "no"
                }}</template>
              </td>
              <td class="num">{{ num(t.evidence.citations_dropped) }}</td>
              <td>
                <span
                  :class="[
                    'status',
                    t.status === 'ok' ? 'status--ok' : 'status--error',
                  ]"
                  >{{ t.status }}</span
                >
              </td>
            </tr>
            <tr v-if="turns.length === 0">
              <td colspan="8" class="muted">
                No turns in the last {{ TURNS_DAYS }} days.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </FoldSection>
  </div>
</template>

<style scoped>
.drawer__fold {
  margin-top: var(--space-3);
}
.hint {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-muted);
  line-height: 1.4;
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.8rem;
}
.mono {
  font-family: var(--font-mono, monospace);
}
.gap-text {
  color: var(--color-error);
  font-weight: 600;
}
.fw-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.fw {
  font-family: var(--font-mono, monospace);
  font-size: 0.68rem;
  padding: 0.05rem 0.35rem;
  border-radius: 3px;
  background: color-mix(in srgb, var(--color-ink) 7%, transparent);
  color: var(--color-muted);
  white-space: nowrap;
}
.table-wrap {
  overflow-x: auto;
  max-width: 100%;
  /* A flex item defaults to `min-width: auto`, which means it refuses to
     shrink below its content — so the wrapper grew to the table's full
     width and `overflow-x` never engaged. The evidence table (611px) was
     dragging the whole drawer sideways at phone width instead of scrolling
     inside its own box. */
  min-width: 0;
}
.wrap {
  overflow-wrap: anywhere;
}
.admin-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.status--ok {
  color: var(--color-success);
  font-weight: 600;
}
.status--error {
  color: var(--color-error);
  font-weight: 600;
}
.drawer {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}
.drawer__section {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  /* Same reason as `.table-wrap`: without this the section inherits its
     widest child's intrinsic width rather than the drawer's. */
  min-width: 0;
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border);
}
.drawer__section:first-of-type {
  padding-top: 0;
  border-top: 0;
}
.drawer__section h4 {
  margin: 0;
  font-size: 0.95rem;
}
.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.grid2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: var(--space-3);
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.field small {
  font-size: 0.75rem;
}
.field input[type="text"],
.field input[type="date"],
.field select,
.field textarea {
  font-size: 0.92rem;
  font-family: inherit;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  min-width: 0;
}
.checks {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.2rem;
}
.check {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  color: var(--color-ink);
  cursor: pointer;
  user-select: none;
  background: var(--color-surface);
}
.check input[type="checkbox"] {
  margin: 0;
}
.check--on {
  border-color: var(--color-accent);
  background: color-mix(in srgb, var(--color-accent) 12%, transparent);
}
.readonly,
.counts {
  margin: 0;
  display: grid;
  gap: 0.5rem var(--space-3);
}
.readonly {
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
}
.readonly dt,
.counts dt {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.readonly dd,
.counts dd {
  margin: 0;
  font-size: 0.95rem;
  color: var(--color-ink);
  font-variant-numeric: tabular-nums;
}
.controls {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.control {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
}
.control__body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}
.control__title {
  font-weight: 600;
  color: var(--color-ink);
}
.approval__state {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.4;
  color: var(--color-ink);
}
.warn-text {
  color: var(--color-warn-ink);
  font-weight: 600;
}
.approval__form {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.approval__field {
  flex: 1 1 14rem;
  min-width: 0;
}
.counts {
  grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
}
</style>
